import { TFunction } from "i18next"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CapabilityIdentifier } from "schema"

type CapabilityProperties = {
  name: string
  description: string
  detailedDescription: string | null
}

function getCapabilitiesDictionary(t: TFunction) {
  const orderTracking = {
    name: t("capabilities.catalog.GetOrderTracking.name"),
    description: t("capabilities.catalog.GetOrderTracking.description"),
    detailedDescription: t("capabilities.catalog.GetOrderTracking.detailedDescription", {
      defaultValue: null,
    }),
  }

  return {
    [CapabilityIdentifier.FindOrderNumber]: {
      name: t("capabilities.catalog.FindOrderNumber.name"),
      description: t("capabilities.catalog.FindOrderNumber.description"),
      detailedDescription: t("capabilities.catalog.FindOrderNumber.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.GetOrderTracking]: orderTracking,
    [CapabilityIdentifier.GetOrderRefundStatus]: {
      name: t("capabilities.catalog.GetOrderRefundStatus.name"),
      description: t("capabilities.catalog.GetOrderRefundStatus.description"),
      detailedDescription: t("capabilities.catalog.GetOrderRefundStatus.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.CancelOrder]: {
      name: t("capabilities.catalog.CancelOrder.name"),
      description: t("capabilities.catalog.CancelOrder.description"),
      detailedDescription: t("capabilities.catalog.CancelOrder.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.Webhook]: {
      name: t("capabilities.catalog.Webhook.name"),
      description: t("capabilities.catalog.Webhook.description"),
      detailedDescription: t("capabilities.catalog.Webhook.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.Instruction]: {
      name: t("capabilities.catalog.Instruction.name"),
      description: t("capabilities.catalog.Instruction.description"),
      detailedDescription: t("capabilities.catalog.Instruction.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.SearchCatalog]: {
      name: t("capabilities.catalog.SearchCatalog.name"),
      description: t("capabilities.catalog.SearchCatalog.description"),
      detailedDescription: t("capabilities.catalog.SearchCatalog.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.ProductInformation]: {
      name: t("capabilities.catalog.ProductInformation.name"),
      description: t("capabilities.catalog.ProductInformation.description"),
      detailedDescription: t("capabilities.catalog.ProductInformation.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.MCP]: {
      name: t("capabilities.catalog.MCP.name"),
      description: t("capabilities.catalog.MCP.description"),
      detailedDescription: t("capabilities.catalog.MCP.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.BusinessOpportunity]: {
      name: t("capabilities.catalog.BusinessOpportunity.name"),
      description: t("capabilities.catalog.BusinessOpportunity.description"),
      detailedDescription: t("capabilities.catalog.BusinessOpportunity.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.ReactToMessage]: {
      name: t("capabilities.catalog.ReactToMessage.name"),
      description: t("capabilities.catalog.ReactToMessage.description"),
      detailedDescription: t("capabilities.catalog.ReactToMessage.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.Support]: {
      name: t("capabilities.catalog.Support.name"),
      description: t("capabilities.catalog.Support.description"),
      detailedDescription: t("capabilities.catalog.Support.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.RequestReview]: {
      name: t("capabilities.catalog.RequestReview.name"),
      description: t("capabilities.catalog.RequestReview.description"),
      detailedDescription: t("capabilities.catalog.RequestReview.detailedDescription", {
        defaultValue: null,
      }),
    },
    [CapabilityIdentifier.UpdateOrder]: {
      name: t("capabilities.catalog.UpdateOrder.name"),
      description: t("capabilities.catalog.UpdateOrder.description"),
      detailedDescription: t("capabilities.catalog.UpdateOrder.detailedDescription", {
        defaultValue: null,
      }),
    },
  } satisfies Record<keyof typeof CapabilityIdentifier, CapabilityProperties>
}

export function useCapabilitiesDictionary() {
  const { t } = useTranslation()
  return useMemo(() => getCapabilitiesDictionary(t), [t])
}
